import { Component, ViewEncapsulation, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { DepositService } from 'src/app/services/deposit/deposit.service';
import { ClientService } from 'src/app/services/user/client.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import swal from 'sweetalert2';

@Component({
  selector: 'app-payment-allocations',
  templateUrl: './payment-allocations.component.html',
  styleUrls: ['./payment-allocations.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PaymentAllocationsComponent extends DestructorComponent implements OnInit {

  @ViewChild('invoiceListing') invoiceListingComponent: any;
  @ViewChild('depositListing') depositListingComponent: any;

  clientData: any;
  customerId: string | null = null;
  deposits: any[] = [];
  invoices: any[] = [];
  selectedDeposit: any = null;
  allocationData: any[] = [];
  hasAllocations: boolean = false;
  isLoading: boolean = false;

  // Customer search properties
  customerSearchTerm: string = '';
  selectedCustomer: any = null;
  customerSearchResults: any[] = [];
  showCustomerDropdown: boolean = false;
  isCustomerSearching: boolean = false;
  private customerSearchSubject = new Subject<string>();

  // Filter properties
  showFilterSection: boolean = false;
  filterForm: FormGroup;
  activeFilters: any = {};

  constructor(
    private _clientService: ClientService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _depositService: DepositService,
    private _formBuilder: FormBuilder
  ) {
    super();
    this.customerId = this._route.snapshot.paramMap.get('customerId');
    this.setupCustomerSearchDebounce();
    this.initializeFilterForm();
  }

  ngOnInit(): void {
    // Subscribe to route parameter changes
    this.subscriptions.push(
      this._route.paramMap.subscribe(params => {
        this.customerId = params.get('customerId');
        if (this.customerId) {
          this.loadClientData();
          // Clear previous selections when customer changes
          this.selectedDeposit = null;
          this.allocationData = [];
          this.hasAllocations = false;
        }
      })
    );
  }

  loadClientData(): void {
    if (!this.customerId) {
      console.error('No customer ID provided');
      return;
    }

    this.subscriptions.push(
      this._clientService.GetDataById(this.customerId).subscribe({
        next: (response) => {
          if (response.status && response.data) {
            this.clientData = response.data;
            this.deposits = this.clientData.deposits || [];
            this.invoices = this.clientData.invoices || [];
            console.log('Client data loaded:', this.clientData);
          }
        },
        error: (error) => {
          console.error('Error loading client data:', error);
        }
      })
    );
  }

  onDepositSelected(deposit: any): void {
    this.selectedDeposit = deposit;
    console.log('Selected deposit:', deposit);
  }

  onAllocationChanged(allocations: any[]): void {
    this.allocationData = allocations;
    this.hasAllocations = allocations.length > 0;
  }

  onAllocationDeleted(): void {
    if (this.depositListingComponent) {
      this.depositListingComponent.GetData();
    }
  }

  saveAllocations(): void {

    if (!this.allocationData.length) {
      console.warn('No allocations to save');
      return;
    }

    if (!this.selectedDeposit) {
      swal.fire({
        text: "Please select a deposit to allocate.",
        icon: "warning",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
      return;
    }

    this.isLoading = true;

    let data = {
      deposit_id: this.selectedDeposit._id,
      allocations: this.allocationData
    };

    this.subscriptions.push(
      this._depositService.AllocateAmount(data).subscribe({
        next: () => {
          setTimeout(() => {
            this.isLoading = false;
            this.loadClientData();

            // Clear allocation inputs and refresh child components
            if (this.invoiceListingComponent) {
              this.invoiceListingComponent.clearAllocations();
              this.invoiceListingComponent.GetData();
              this.invoiceListingComponent.GetCreditDebitData();
            }
            if (this.depositListingComponent) {
              this.depositListingComponent.GetData();
              this.depositListingComponent.GetCreditDebitData();
            }

          }, 1000);
          swal.fire({
            text: "Allocations saved successfully!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });

          this.allocationData = [];
          this.hasAllocations = false;

        },
        error: (error) => {

          let message = error.error?.message || error.message || "Sorry, looks like there are some errors detected, please try again.";

          swal.fire({
            text: message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
          this.isLoading = false;
        }
      })
    );
  }

  // Customer search methods
  private setupCustomerSearchDebounce(): void {
    this.customerSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchCustomers(searchTerm);
    });
  }

  onCustomerSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.customerSearchTerm = target.value;
    this.selectedCustomer = null;
    this.customerSearchSubject.next(this.customerSearchTerm);
    this.showCustomerDropdown = true;
  }

  onCustomerInputFocus(): void {
    if (this.customerSearchResults.length > 0) {
      this.showCustomerDropdown = true;
    }
  }

  onCustomerInputBlur(): void {
    setTimeout(() => {
      this.showCustomerDropdown = false;
    }, 200);
  }

  private searchCustomers(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.customerSearchResults = [];
      this.showCustomerDropdown = false;
      return;
    }

    this.isCustomerSearching = true;
    this.subscriptions.push(
      this._clientService.GetData('1', searchTerm).subscribe({
        next: (response: any) => {
          this.isCustomerSearching = false;
          if (response.status && response.data && response.data.data) {
            this.customerSearchResults = response.data.data;
            this.showCustomerDropdown = this.customerSearchResults.length > 0;
          } else {
            this.customerSearchResults = [];
            this.showCustomerDropdown = false;
          }
        },
        error: (error: any) => {
          this.isCustomerSearching = false;
          console.error('Error searching customers:', error);
          this.customerSearchResults = [];
          this.showCustomerDropdown = false;
        }
      })
    );
  }

  selectCustomer(customer: any): void {
    this.selectedCustomer = customer;
    this.customerSearchTerm = this.getCustomerDisplayName(customer);
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];

    // Navigate to the same route with new customer ID
    this._router.navigate(['/sales/sales-payment/allocation', customer._id || customer.id]);
  }

  clearCustomerSearch(): void {
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
  }

  getCustomerDisplayName(customer: any): string {

    if (customer.name && customer.company) {
      return `${customer.name}, ${customer.company}, ${customer.currency_type}`;
    }

    return `${customer.name || ''} ${customer.last_name || ''}`.trim() || customer.email || 'Unknown Customer';
  }

  // Filter methods
  private initializeFilterForm(): void {
    this.filterForm = this._formBuilder.group({
      stockNumber: [''],
      balanceFinalizedDateFrom: [''],
      balanceFinalizedDateTo: [''],
      keyword: ['']
    });
  }

  toggleFilterSection(): void {
    this.showFilterSection = !this.showFilterSection;
  }

  applyFilters(): void {
    const filterValues = this.filterForm.value;
    console.log('Applied Filters:', filterValues);

    // Log only non-empty filter values
    this.activeFilters = Object.keys(filterValues)
      .filter(key => filterValues[key] && filterValues[key].toString().trim() !== '')
      .reduce((obj, key) => {
        obj[key] = filterValues[key];
        return obj;
      }, {} as any);

    console.log('Active Filters:', this.activeFilters);

    // TODO: Implement API call with filters
    // This will be implemented later as requested
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.activeFilters = {};
    console.log('Filters cleared');
  }
}
